import React, { useState, useEffect, useCallback, useMemo } from "react";
import CommentsService from "../../services/CommentsService";
import UserProfileService from "../../services/UserProfileService";
import UsersService from "../../services/UsersService";
import { ChatBubble, Search, Add } from "@mui/icons-material";

const getNameFromProfile = (profile) => {
  if (!profile) return "";
  if (typeof profile === "string") return profile;
  const firstName = profile.firstName ?? profile.FirstName ?? profile.nombre ?? profile.Nombre ?? "";
  const lastName = profile.lastName ?? profile.LastName ?? profile.apellido ?? profile.Apellido ?? "";
  const fullFromParts = `${firstName} ${lastName}`.trim();
  return (
    profile.name ??
    profile.Name ??
    profile.fullName ??
    profile.FullName ??
    profile.displayName ??
    profile.DisplayName ??
    profile.userName ??
    profile.UserName ??
    fullFromParts
  );
};

const extractAuthorId = (c) => {
  if (!c) return null;
  const author = c.author ?? c.Author;
  const user = c.user ?? c.User;

  if (author != null && typeof author === "object") {
    return author.id ?? author.Id ?? null;
  }
  if (user != null && typeof user === "object") {
    return user.id ?? user.Id ?? null;
  }

  const rawId =
    c.userId ??
    c.UserId ??
    c.authorId ??
    c.AuthorId ??
    author ??
    user;

  return rawId != null && rawId !== "" ? rawId : null;
};

export default function Comments({ videoId, courseId }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [replyText, setReplyText] = useState({});
  const [replies, setReplies] = useState({});
  const [authorNames, setAuthorNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const userProfileService = useMemo(() => new UserProfileService(), []);

  // Normaliza texto y autor según distintas formas que puede devolver el API (camelCase / PascalCase)
  const getCommentText = (c) => {
    if (!c) return "";
    const raw =
      c.content ??
      c.text ??
      c.Content ??
      c.Text ??
      c.message ??
      c.Message ??
      "";
    return String(raw);
  };

  const getAuthorLabel = useCallback((c) => {
    if (!c) return "";
    const author = c.author ?? c.Author;
    const user = c.user ?? c.User;

    const fromObject = getNameFromProfile(author) || getNameFromProfile(user);
    if (fromObject) return fromObject;

    const directName =
      c.authorName ??
      c.AuthorName ??
      c.userName ??
      c.UserName;
    if (directName) return String(directName);

    const authorId = extractAuthorId(c);
    if (authorId != null) {
      const cachedName = authorNames[String(authorId)];
      if (cachedName) return cachedName;

      try {
        const storedUser = JSON.parse(localStorage.getItem("usuarioConCedula") || "null");
        if (storedUser && String(storedUser.id) === String(authorId)) {
          return getNameFromProfile(storedUser) || "Usuario";
        }
      } catch {
        // ignore parse errors
      }
    }

    return "";
  }, [authorNames]);

  const resolveAuthorNames = useCallback(async (commentsList) => {
    const namesFromComments = {};
    const idsToFetch = new Set();

    for (const comment of commentsList || []) {
      const authorId = extractAuthorId(comment);
      if (authorId == null) continue;

      const idKey = String(authorId);
      const author = comment?.author ?? comment?.Author;
      const user = comment?.user ?? comment?.User;
      const fromObject = getNameFromProfile(author) || getNameFromProfile(user);

      if (fromObject) {
        namesFromComments[idKey] = fromObject;
      } else {
        idsToFetch.add(idKey);
      }
    }

    const profileById = { ...namesFromComments };

    await Promise.all(
      [...idsToFetch].map(async (id) => {
        if (profileById[id]) return;

        try {
          const storedUser = JSON.parse(localStorage.getItem("usuarioConCedula") || "null");
          if (storedUser && String(storedUser.id) === id) {
            profileById[id] = getNameFromProfile(storedUser);
            return;
          }
        } catch {
          // ignore parse errors
        }

        try {
          const profile = await userProfileService.getUserById(id);
          profileById[id] = getNameFromProfile(profile);
        } catch {
          try {
            const profile = await UsersService.getUser(id);
            profileById[id] = getNameFromProfile(profile);
          } catch {
            profileById[id] = "";
          }
        }
      })
    );

    setAuthorNames((prev) => ({ ...prev, ...profileById }));
  }, [userProfileService]);

  const getCommentId = (c) => c?.id ?? c?.Id;

  // Función para formatear fechas relativas
  const formatRelativeTime = (dateString) => {
    if (!dateString) return "";
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return "Hace unos momentos";
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `Hace ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `Hace ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return `Hace ${diffInDays} ${diffInDays === 1 ? 'día' : 'días'}`;
    }
    
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `Hace ${diffInMonths} ${diffInMonths === 1 ? 'mes' : 'meses'}`;
    }
    
    const diffInYears = Math.floor(diffInMonths / 12);
    return `Hace ${diffInYears} ${diffInYears === 1 ? 'año' : 'años'}`;
  };

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const commentsData = await CommentsService.getComments(null, courseId);
      
      // Separate parent comments from replies
      const parentComments = [];
      const repliesData = {};
      
      for (const comment of commentsData || []) {
        const parentId =
          comment.parentCommentId ?? comment.ParentCommentId ?? comment.parentId ?? comment.ParentId;
        const cid = getCommentId(comment);
        if (!parentId) {
          parentComments.push(comment);
          repliesData[cid] = [];
        } else {
          if (!repliesData[parentId]) {
            repliesData[parentId] = [];
          }
          repliesData[parentId].push(comment);
        }
      }
      
      // If no replies were found in the main response, try fetching them separately
      const hasAnyReplies = Object.values(repliesData).some(replies => replies.length > 0);
      
      if (!hasAnyReplies && parentComments.length > 0) {
        for (const comment of parentComments) {
          const cid = getCommentId(comment);
          try {
            const commentReplies = await CommentsService.getReplies(cid, courseId);
            repliesData[cid] = commentReplies || [];
          } catch (err) {
            console.warn(`Failed to load replies for comment ${cid}:`, err);
            repliesData[cid] = [];
          }
        }
      }
      
      setComments(parentComments);
      setReplies(repliesData);

      const allComments = [
        ...parentComments,
        ...Object.values(repliesData).flat(),
      ];
      await resolveAuthorNames(allComments);
    } catch (error) {
      console.error('Error loading comments:', error);
      setError('Error al cargar los comentarios');
    } finally {
      setLoading(false);
    }
  }, [courseId, resolveAuthorNames]);

  // Load comments when component mounts or when courseId changes
  useEffect(() => {
    if (courseId) {
      loadComments();
    }
  }, [courseId, loadComments]);

  const handleAddComment = async () => {
    if (newComment.trim() !== "" && courseId) {
      try {
        setLoading(true);
        await CommentsService.addComment(null, courseId, newComment);
        setNewComment("");
        await loadComments();
      } catch (error) {
        console.error('Error adding comment:', error);
        setError('Error al agregar el comentario');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleReply = async (commentId) => {
    const replyTextValue = replyText[commentId]?.trim();
    if (replyTextValue && courseId) {
      try {
        setLoading(true);
        await CommentsService.addReply(commentId, courseId, replyTextValue);
        setReplyText(prev => ({ ...prev, [commentId]: "" }));
        await loadComments();
      } catch (error) {
        console.error('Error adding reply:', error);
        setError('Error al agregar la respuesta');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleReplyTextChange = (commentId, value) => {
    setReplyText(prev => ({ ...prev, [commentId]: value }));
  };

  // Calcular total de comentarios (padres + respuestas)
  const totalComments = useMemo(() => {
    const totalReplies = Object.values(replies).reduce((sum, replyList) => sum + replyList.length, 0);
    return comments.length + totalReplies;
  }, [comments, replies]);

  // Filtrar comentarios según búsqueda (incluye coincidencias en respuestas)
  const filteredComments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      return comments;
    }

    return comments.filter((comment) => {
      const content = getCommentText(comment).toLowerCase();
      const authorName = getAuthorLabel(comment).toLowerCase();
      if (content.includes(q) || authorName.includes(q)) {
        return true;
      }
      const replyList = replies[getCommentId(comment)] || [];
      return replyList.some((reply) => {
        const rt = getCommentText(reply).toLowerCase();
        const ra = getAuthorLabel(reply).toLowerCase();
        return rt.includes(q) || ra.includes(q);
      });
    });
  }, [comments, searchQuery, replies, getAuthorLabel]);

  // Show loading state if no courseId provided
  if (!courseId) {
    return (
      <div className="w-full bg-white rounded-lg border">
        <div className="background-green-aqua px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-white uppercase">Comentarios / Preguntas y Respuestas</h3>
        </div>
        <div className="p-4">
          <p className="text-center text-gray-400">Cargando información del curso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-lg border overflow-hidden">
      {/* Header con fondo teal */}
      <div className="background-green-aqua px-6 py-4 flex items-center justify-between">
        <h3 className="text-xl font-bold text-white uppercase">Comentarios / Preguntas y Respuestas</h3>
        <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1">
          <ChatBubble className="text-white" style={{ fontSize: '20px' }} />
          <span className="text-white font-semibold">{totalComments}</span>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {error && (
          <div className="px-4 py-3 text-red-700 bg-red-100 rounded border border-red-400">
            {error}
          </div>
        )}

        {/* Input para nuevo comentario */}
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 px-4 py-3 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-aqua focus:border-transparent"
            placeholder="Deja tu comentario o pregunta."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddComment();
              }
            }}
            disabled={loading}
          />
          <button
            onClick={handleAddComment}
            disabled={loading || !newComment.trim()}
            className="background-green-aqua text-white px-4 py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[50px]"
          >
            <Add className="text-white" />
          </button>
        </div>

        {/* Barra de búsqueda */}
        <div className="relative">
          <input
            type="text"
            className="w-full px-4 py-2 pr-10 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-aqua focus:border-transparent"
            placeholder="Buscar"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" style={{ fontSize: '20px' }} />
        </div>

        {/* Título de la sección de comentarios */}
        <h4 className="text-center font-bold text-gray-900 text-lg">
          Todas las preguntas y comentarios de este curso
        </h4>

        {/* Lista de comentarios */}
        {loading && (
          <div className="py-8 text-center">
            <p className="text-gray-500">Cargando comentarios...</p>
          </div>
        )}

        {!loading && filteredComments.length === 0 && (
          <p className="text-center text-gray-400 py-8">
            {searchQuery ? 'No se encontraron comentarios que coincidan con tu búsqueda.' : 'No hay comentarios aún. Sé el primero en comentar 😉.'}
          </p>
        )}

        <div className="space-y-4">
          {filteredComments.map((comment) => {
            const cid = getCommentId(comment);
            const commentReplies = replies[cid] || [];
            const q = searchQuery.trim().toLowerCase();
            const parentMatches =
              !q ||
              getCommentText(comment).toLowerCase().includes(q) ||
              getAuthorLabel(comment).toLowerCase().includes(q);
            const visibleReplies =
              !q || parentMatches
                ? commentReplies
                : commentReplies.filter((reply) => {
                    const rt = getCommentText(reply).toLowerCase();
                    const ra = getAuthorLabel(reply).toLowerCase();
                    return rt.includes(q) || ra.includes(q);
                  });
            const replyCount = commentReplies.length;

            return (
              <div key={cid} className="border-b border-gray-200 pb-4 last:border-b-0">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="font-bold text-gray-900">
                        {getAuthorLabel(comment) || "Usuario"}
                      </p>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">
                      {formatRelativeTime(comment.createdAt ?? comment.CreatedAt)}
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      {getCommentText(comment)}
                    </p>
                    
                    {/* Input para responder */}
                    <div className="flex gap-2 mt-3">
                      <input
                        type="text"
                        className="flex-1 px-3 py-2 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-aqua focus:border-transparent text-sm"
                        placeholder="Responder..."
                        value={replyText[cid] || ""}
                        onChange={(e) => handleReplyTextChange(cid, e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleReply(cid);
                          }
                        }}
                        disabled={loading}
                      />
                      <button
                        onClick={() => handleReply(cid)}
                        disabled={loading || !replyText[cid]?.trim()}
                        className="background-green-aqua text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        Responder
                      </button>
                    </div>
                  </div>
                  
                  {/* Indicador de respuestas */}
                  <div className="flex items-center gap-1 text-green-aqua flex-shrink-0">
                    <ChatBubble style={{ fontSize: '20px' }} />
                    <span className="font-semibold">{replyCount}</span>
                  </div>
                </div>

                {/* Mostrar respuestas */}
                {visibleReplies.length > 0 && (
                  <div className="ml-4 pl-4 border-l-2 border-gray-200 space-y-3 mt-3">
                    {visibleReplies.map((reply) => (
                      <div key={getCommentId(reply) ?? reply.id} className="pt-2">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-sm text-gray-900">
                            {getAuthorLabel(reply) || "Usuario"}
                          </p>
                          <span className="text-xs text-gray-400">•</span>
                          <p className="text-xs text-gray-500">
                            {formatRelativeTime(reply.createdAt ?? reply.CreatedAt)}
                          </p>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {getCommentText(reply)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
